import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { BrandMark } from "../../components/Logo";
import Avatar from "../../components/Avatar";
import { Home, Package, ClipboardList, LogOut as LogOutIcon, Camera, Loader2, HelpCircle } from "lucide-react";
import { WhatsAppSupport } from "../../components/WhatsAppSupport";
import { Button } from "../../components/ui/button";
import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

const tabs = [
  { to: "/promoter", label: "Home", icon: Home, end: true, key: "home" },
  { to: "/promoter/stock", label: "Stock", icon: Package, key: "stock" },
  { to: "/promoter/survey", label: "Survey", icon: ClipboardList, key: "survey" },
  { to: "/promoter/surveys", label: "Logged", icon: ClipboardList, key: "surveys" },
  { to: "/promoter/faq", label: "FAQ", icon: HelpCircle, key: "faq" },
];

export default function PromoterLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const isCheckPage = loc.pathname.includes("check-");
  const [avatarPath, setAvatarPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api
      .get("/promoter/me")
      .then((r) => setAvatarPath(r.data?.user?.avatar_path || null))
      .catch(() => {});
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();

  const onPickAvatar = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large (max 8MB)");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const r = await api.post("/promoter/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAvatarPath(r.data.avatar_path);
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-20 glass-bar">
        <div className="px-4 h-14 flex items-center justify-between">
          <BrandMark compact />
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="rounded-xl h-9 text-neutral-600"
            data-testid="promoter-logout"
          >
            <LogOutIcon className="h-4 w-4" />
          </Button>
        </div>
        <div className="px-4 pb-3 flex items-center gap-3">
          <div className="relative shrink-0">
            <Avatar path={avatarPath} name={user?.name} size={48} className="rounded-full ring-2 ring-white/80 shadow-md" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-md ring-2 ring-white"
              aria-label="Change profile photo"
              data-testid="avatar-upload-button"
            >
              {uploading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Camera className="h-3 w-3" />
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickAvatar}
              data-testid="avatar-upload-input"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-400">
              {greeting}
            </div>
            <div className="font-display font-bold text-lg truncate" data-testid="promoter-name">
              {user?.name || "—"}
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 pt-4 pb-28 max-w-2xl w-full mx-auto">
        <Outlet />
        <div className="mt-8 flex justify-center">
          <WhatsAppSupport />
        </div>
      </main>

      {!isCheckPage && (
        <nav className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pointer-events-none">
          <div className="max-w-2xl mx-auto glass-panel rounded-3xl grid grid-cols-5 overflow-hidden pointer-events-auto">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <NavLink
                  key={t.key}
                  to={t.to}
                  end={t.end}
                  data-testid={`promoter-nav-${t.key}`}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-3 text-[10px] uppercase tracking-widest transition-colors ${
                      isActive ? "text-slate-900 font-semibold" : "text-slate-400"
                    }`
                  }
                >
                  <Icon className="h-5 w-5 mb-1" />
                  {t.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
